import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Database, FileText, LogOut, Layers, Upload, UserCheck, BarChart3, Key, Settings, MapPin, Megaphone, BookOpen, FileCheck, Activity } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";

const NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/clients', icon: Building2, label: 'Établissements' },
  { to: '/sectors', icon: Layers, label: 'Secteurs' },
  { to: '/data', icon: Database, label: 'Saisie données' },
  { to: '/import', icon: Upload, label: 'Import CSV' },
  { to: '/reports', icon: FileText, label: 'Rapports' },
  { to: '/pending', icon: UserCheck, label: 'Inscriptions' },
  { to: '/analytics', icon: BarChart3, label: 'Analytiques' },
  { to: '/cartographie', icon: MapPin, label: 'Cartographie' },
  { to: '/kpis', icon: Settings, label: 'KPIs' },
  { to: '/admin/campagnes', icon: Megaphone, label: 'Campagnes' },
  { to: '/admin/cabinet/prompts', icon: BookOpen, label: 'Cabinet · Prompts' },
  { to: '/admin/cabinet/revue', icon: FileCheck, label: 'Cabinet · Revue' },
  { to: '/admin/veille-proactive/health', icon: Activity, label: 'Veille · Santé' },
];

export default function AdminLayout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  const handleLogout = () => {
    localStorage.removeItem('benchory_token');
    localStorage.removeItem('benchory_user');
    onLogout();
    navigate('/login');
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { setPwMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
    if (pwForm.newPw.length < 6) { setPwMsg({ type: 'error', text: 'Minimum 6 caractères' }); return; }
    setPwLoading(true);
    setPwMsg({ type: '', text: '' });
    try {
      const token = localStorage.getItem('benchory_token');
      await axios.post(`${API_URL}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers: { Authorization: `Bearer ${token}` } });
      setPwMsg({ type: 'success', text: 'Mot de passe modifié' });
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => setShowChangePw(false), 2000);
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    }
    setPwLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex" data-testid="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-100 flex flex-col shadow-[1px_0_3px_rgba(0,0,0,0.02)] shrink-0 transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <img src={LOGO_URL} alt="BENCHORY" className="h-10" data-testid="sidebar-logo" />
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 text-gray-400">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-[#1B2A4A] text-white shadow-sm'
                    : 'text-gray-500 hover:text-[#1B2A4A] hover:bg-gray-50'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-100">
          <div className="px-4 py-2 mb-2">
            <p className="text-xs font-semibold text-[#1B2A4A] truncate">{user?.name}</p>
            <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => setShowChangePw(true)}
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-[#B8860B] hover:bg-amber-50 transition-all w-full"
            data-testid="admin-change-password-btn"
          >
            <Key size={18} />
            Modifier mot de passe
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all w-full"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Déconnexion
          </button>
        </div>
      </aside>
      {/* Mobile header */}
      <div className="flex-1 flex flex-col min-h-screen">
        <div className="lg:hidden bg-white border-b border-gray-100 px-4 h-14 flex items-center gap-3 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="p-2 text-gray-500 hover:text-[#1B2A4A]" data-testid="mobile-menu-btn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <img src={LOGO_URL} alt="BENCHORY" className="h-7" />
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      {showChangePw && (
        <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => setShowChangePw(false)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="admin-change-password-modal">
            <div className="flex items-center gap-2 mb-4">
              <Key size={18} className="text-[#B8860B]" />
              <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope">Modifier le mot de passe</h2>
            </div>
            {pwMsg.text && (
              <div className={`px-3 py-2 rounded-lg mb-4 text-sm ${pwMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{pwMsg.text}</div>
            )}
            <form onSubmit={handleChangePw} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Mot de passe actuel</label>
                <input data-testid="admin-pw-current" type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Nouveau mot de passe</label>
                <input data-testid="admin-pw-new" type="password" value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Confirmer</label>
                <input data-testid="admin-pw-confirm" type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                  className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" required />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowChangePw(false)} className="flex-1 h-9 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all">Annuler</button>
                <button data-testid="admin-pw-submit" type="submit" disabled={pwLoading}
                  className="flex-1 h-9 bg-[#1B2A4A] text-white rounded-lg text-sm font-semibold hover:bg-[#152039] transition-all disabled:opacity-50">
                  {pwLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : 'Modifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
